import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { TopLevelModes } from "@/lib/modes";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

interface SearchSelectGroupProps {
  setPeriod: (period: string) => void;
  setMode: (mode: string) => void;
  setDate: (dateRange: DateRange | undefined) => void;
}

export function SearchSelectGroup({
  setPeriod,
  setMode,
  setDate,
}: SearchSelectGroupProps) {
  const { t } = useTranslation(["common"]);

  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [selectedMode, setSelectedMode] = useState("all"); // New state for mode
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [showCustomDate, setShowCustomDate] = useState(false);

  useEffect(() => {
    setPeriod(selectedPeriod);
  }, [selectedPeriod, setPeriod]);

  useEffect(() => {
    setMode(selectedMode);
  }, [selectedMode, setMode]);

  useEffect(() => {
    if (dateRange) {
      setDateRange(dateRange);
      setDate(dateRange);
    } else {
      setDateRange(undefined);
      setDate(dateRange);
    }
  }, [dateRange, setDateRange]);

  const handlePeriodChange = (value: string) => {
    if (value === "custom") {
      setShowCustomDate(true);
    } else {
      setShowCustomDate(false);
      setSelectedPeriod(value);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
      {" "}
      {/* Mode Selector */}
      <Select value={selectedMode} onValueChange={setSelectedMode}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="Select mode" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            {t("common:filterControls.modeSelector.all")}
          </SelectItem>
          {Object.values(TopLevelModes).map(
            (mode) =>
              mode !== TopLevelModes.Translate && (
                <SelectItem key={mode} value={mode}>
                  {mode}
                </SelectItem>
              ),
          )}
        </SelectContent>
      </Select>
      {/* Period Selector */}
      <Select
        value={showCustomDate ? "custom" : selectedPeriod}
        onValueChange={handlePeriodChange}
      >
        <SelectTrigger className="w-[180px]">
          <CalendarIcon size={16} />
          <SelectValue placeholder="Select period" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">
            {t("common:filterControls.periodSelector.all")}
          </SelectItem>
          <SelectItem value="7">
            {t("common:filterControls.periodSelector.7Days")}
          </SelectItem>
          <SelectItem value="30">
            {t("common:filterControls.periodSelector.30Days")}
          </SelectItem>
          <SelectItem value="90">
            {t("common:filterControls.periodSelector.90Days")}
          </SelectItem>
          {/* <SelectItem value="custom">Custom range</SelectItem> */}
        </SelectContent>
      </Select>
      {showCustomDate && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-[280px] justify-start text-left font-normal bg-transparent"
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "LLL dd, y")} -{" "}
                    {format(dateRange.to, "LLL dd, y")}
                  </>
                ) : (
                  format(dateRange.from, "LLL dd, y")
                )
              ) : (
                <span>{t("common:filterControls.periodSelector.custom")}</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              initialFocus
              mode="range"
              defaultMonth={dateRange?.from}
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
